import { cx } from '@twind/core';
import { ChangeEventHandler, useState } from 'react';
import OstroDynamicContent from 'app/components/OstroDynamicContent';

import { useContactFormLinkClick } from '../hooks/useContactFormLinkClick';
import { FormItemSingleChoice } from '../types';

type Props = { item: FormItemSingleChoice; isSubmitted: boolean; contactFormSlug?: string };

export function ContactFormMultiChoice({ item, isSubmitted, contactFormSlug }: Props) {
  const { handleLinkClick } = useContactFormLinkClick(contactFormSlug);
  const hasErrors = !!item.errors?.length;
  const showError = isSubmitted && hasErrors;

  const errorId = `contact-form-multi-choice-error-${item.ref_id}`;
  const labelId = `contact-form-multi-choice-label-${item.ref_id}`;

  return (
    <div className="ostro-contact-form-multi-choice flex flex-col gap-2">
      <div className="flex items-center text-sm gap-1">
        <div
          id={labelId}
          className="ostro-contact-form-multi-choice-label"
          dangerouslySetInnerHTML={{ __html: item.label }}
          onClick={handleLinkClick}
        />
        {!item.required && <span>(optional)</span>}
      </div>
      <div
        className="flex flex-col gap-4"
        role="group"
        aria-labelledby={labelId}
      >
        {item.choices.map((choice: string) => (
          <CheckBox
            key={choice}
            value={item.value as string[]}
            refId={item.ref_id}
            choice={choice}
            showError={showError}
            errorId={errorId}
          />
        ))}
      </div>
      {showError && (
        <div
          className="text-xs -mt-1 text-[#FF3323]"
          id={errorId}
        >
          {item.errors?.join(', ')}
        </div>
      )}
    </div>
  );
}

type CheckBoxProps = {
  refId: string;
  choice: string;
  value: string[];
  showError?: boolean;
  errorId?: string;
};

function CheckBox({ value, refId, choice, showError, errorId }: CheckBoxProps) {
  const [checked, setChecked] = useState(!!value?.includes(choice));

  const handleChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    setChecked(target.checked);
  };

  return (
    <label
      className={cx(
        'flex gap-1 items-center border h-[54px] px-[14px] py-3 rounded-[8px]',
        checked ? 'border-[#F0F0F0]' : 'border-typography-divider',
        checked && 'bg-[#F0F0F0]'
      )}
    >
      <OstroDynamicContent
        component="span"
        className="inline text-base grow"
        content={choice}
      />
      {!checked ? (
        <input
          type="hidden"
          name={refId}
          value=""
        />
      ) : null}
      <input
        className="accent-[#000000] shrink-0 h-4 w-4"
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        name={refId}
        value={choice}
        aria-invalid={showError}
        aria-describedby={showError ? errorId : undefined}
      />
    </label>
  );
}
